import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { 
  Crown, 
  Check, 
  ArrowRight, 
  Sparkles, 
  Star,
  Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useAuthContext } from '../lib/AuthProvider';
import { useRushdMembership, useRushdMembershipStatus } from '../context/RushdMembershipContext';
import { useRushdSettings } from '../context/RushdSettingsContext';
import { useCurrency } from '../context/CurrencyContext';
import { useToast } from '../hooks/use-toast';
import { RUSHD_PLAN_CONFIG, RUSHD_PERKS } from '../types/rushd';
import AuthModalWrapper from '../components/AuthModalWrapper';
import { trackRushdPricingPageVisit, trackRushdSignupConversion } from '../services/visitorTrackingService';

const RushdPricing = () => {
  const { user } = useAuthContext();
  const { createCheckoutSession } = useRushdMembership();
  const { isMember, isActive, membershipStatus } = useRushdMembershipStatus();
  const { settings: rushdSettings } = useRushdSettings();
  const { formatDisplayAmount, getConvertedItemPrice, selectedCurrency } = useCurrency();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const [loadingPlan, setLoadingPlan] = useState<'monthly' | 'yearly' | null>(null);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  
  // Track pricing page visit
  useEffect(() => {
    trackRushdPricingPageVisit();
  }, []);

  // Redirect to home if Rush'd program is disabled
  React.useEffect(() => {
    if (rushdSettings && !rushdSettings.programActive) {
      navigate('/', { replace: true });
    }
  }, [rushdSettings, navigate]);
  
  // Don't render if program is disabled
  if (!rushdSettings?.programActive) {
    return null;
  }
  
  const handlePlanSelect = async (plan: 'monthly' | 'yearly') => {
    if (!user) {
      setAuthModalOpen(true);
      return;
    }
    
    if (isMember && isActive) {
      toast({
        title: 'Already a Member',
        description: 'You already have an active Rush\'d membership.',
        variant: 'default',
      });
      return;
    }
    
    setLoadingPlan(plan);
    
    try {
      const currentUrl = window.location.origin;
      const successUrl = `${currentUrl}/rushd/success?plan=${plan}`;
      const cancelUrl = `${currentUrl}/rushd/pricing`;
      
      const session = await createCheckoutSession(plan, successUrl, cancelUrl);
      
      // Track signup conversion (user initiated checkout)
      if (user?.uid) {
        await trackRushdSignupConversion(user.uid, plan);
      }
      
      // Redirect to Stripe Checkout
      if (session.url) {
        window.location.href = session.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast({
        title: 'Checkout Error',
        description: 'Unable to start checkout process. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoadingPlan(null);
    }
  };
  
  return (
    <>
      <Helmet>
        <title>JamPack'd Rush'd - Premium Membership | JamPack'd Care Packages</title>
        <meta name="description" content="Join JamPack'd Rush'd for discounted Express shipping, priority packing, members-only discounts, and surprise treats in every order." />
        <meta name="keywords" content="jampackd, rushd, membership, express shipping, jamaica, premium" />
        
        {/* Open Graph */}
        <meta property="og:title" content="JamPack'd Rush'd - Premium Membership | JamPack'd Care Packages" />
        <meta property="og:description" content="Join JamPack'd Rush'd for discounted Express shipping, priority packing, members-only discounts, and surprise treats in every order." />
        <meta property="og:type" content="website" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="JamPack'd Rush'd - Premium Membership" />
        <meta name="twitter:description" content="Join JamPack'd Rush'd for discounted Express shipping, priority packing, members-only discounts, and surprise treats in every order." />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 relative overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-20 w-32 h-32 bg-gradient-to-br from-jamaica-green/10 to-jamaica-green/5 rounded-full animate-pulse delay-300"></div>
          <div className="absolute top-40 right-32 w-24 h-24 bg-jamaica-yellow/15 rounded-full animate-bounce delay-500"></div>
          <div className="absolute bottom-32 left-1/4 w-40 h-40 bg-gradient-to-br from-jamaica-yellow/8 to-jamaica-green/8 rounded-full animate-ping delay-700"></div>
          <div className="absolute bottom-20 right-20 w-28 h-28 bg-jamaica-green/12 rounded-full animate-pulse delay-900"></div>
        </div>
        
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Enhanced Hero Section */}
          <div className="mb-12 text-center">
            <div className="inline-flex items-center gap-3 mb-6 sm:mb-4">
              <div className="relative">
                <div className="w-10 h-10 sm:w-14 sm:h-14 bg-gradient-to-br from-jamaica-yellow to-jamaica-yellow/80 rounded-2xl flex items-center justify-center shadow-xl transform rotate-6 sm:hover:-rotate-6 transition-transform duration-500">
                  <Crown className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
                </div>
                <div className="absolute -top-2 -right-2 w-5 h-5 sm:w-6 sm:h-6 bg-jamaica-green rounded-full flex items-center justify-center animate-bounce">
                  <span className="text-[10px] sm:text-xs font-bold text-white">✨</span>
                </div>
              </div>
              <div className="bg-gradient-to-r from-jamaica-green/10 to-jamaica-yellow/10 px-3 py-1.5 sm:px-5 sm:py-2 rounded-2xl border border-jamaica-green/20 shadow-lg">
                <span className="text-xs sm:text-sm font-bold text-jamaica-black">Premium Membership</span>
              </div>
            </div>

            <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-2 sm:mb-4 leading-tight">
              <span className="bg-gradient-to-r from-jamaica-green via-jamaica-yellow to-jamaica-green bg-clip-text text-transparent p-2">
                JamPack'd Rush'd
              </span>
            </h1>
            <p className="hidden sm:block text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Become a member and enjoy a{' '}
              <span className="text-jamaica-yellow font-semibold">premium shopping experience</span>{' '}designed to{' '}
              <span className="text-jamaica-green font-semibold">save you money</span>{' '}and deliver more from yaad.
            </p>
            <p className="sm:hidden text-sm text-gray-600 max-w-md mx-auto leading-relaxed">
              Become a member and enjoy a premium shopping experience designed to save you money and deliver more from yaad.
            </p>
            
            {/* Member Status Banner */}
            {isMember && isActive && (
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-jamaica-green/10 border border-jamaica-green/20 rounded-full mt-6">
                <Check className="w-4 h-4 text-jamaica-green" />
                <span className="text-sm font-medium text-jamaica-green">
                  You're already a Rush'd member! 
                  {membershipStatus?.plan && ` (${membershipStatus.plan === 'yearly' ? 'Yearly' : 'Monthly'} plan)`}
                </span>
              </div>
            )}
          </div>
          
          {/* Pricing Plans */}
          <div className="grid lg:grid-cols-2 gap-8 mb-16 max-w-5xl mx-auto pt-6">
            {/* Monthly Plan */}
            <Card className="relative border-0 shadow-lg hover:shadow-2xl transition-all duration-500 bg-white/80 backdrop-blur-sm group">
              <div className="absolute inset-0 bg-gradient-to-br from-gray-50/50 to-white/50 rounded-2xl"></div>
              <CardContent className="relative p-6 flex flex-col h-full">
                <div className="text-center mb-4">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Monthly</h3>
                  <div className="flex items-baseline justify-center gap-1 mb-2">
                    <span className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                      {formatDisplayAmount(getConvertedItemPrice(RUSHD_PLAN_CONFIG.monthly.price))}
                    </span>
                    <span className="text-gray-500 font-medium">/month</span>
                  </div>
                  <p className="text-xs text-gray-600">{RUSHD_PLAN_CONFIG.monthly.description}</p>
                </div>
                
                {/* Features List */}
                <div className="space-y-3 mb-6 flex-grow">
                  <div className="flex items-center gap-3 p-2.5 bg-gray-50/50 rounded-lg hover:bg-gray-50 transition-colors duration-200">
                    <div className="flex-shrink-0 w-5 h-5 bg-gradient-to-br from-jamaica-green to-jamaica-green/80 rounded-lg flex items-center justify-center shadow-sm">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 text-xs">Discounted Express Shipping</p>
                      <p className="text-xs text-gray-600">Save up to 75% on Express shipping rates</p>
                    </div>
                  </div>
                  {RUSHD_PERKS.map((perk) => (
                    <div key={perk.id} className="flex items-center gap-3 p-2.5 bg-gray-50/50 rounded-lg hover:bg-gray-50 transition-colors duration-200">
                      <div className="flex-shrink-0 w-5 h-5 bg-gradient-to-br from-jamaica-green to-jamaica-green/80 rounded-lg flex items-center justify-center shadow-sm">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 text-xs">{perk.name}</p>
                        <p className="text-xs text-gray-600">{perk.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
                
                <Button 
                  className="w-full bg-gradient-to-r from-gray-900 to-gray-800 hover:from-gray-800 hover:to-gray-700 text-white py-3 text-base font-semibold rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 group mt-auto"
                  onClick={() => handlePlanSelect('monthly')}
                  disabled={loadingPlan === 'monthly' || (isMember && isActive)}
                >
                  {loadingPlan === 'monthly' ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : isMember && isActive && membershipStatus?.plan === 'monthly' ? (
                    'Current Plan'
                  ) : (
                    <>
                      Get Started
                      <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform duration-200" />
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
            
            {/* Yearly Plan - Popular */}
            <div className="relative">
              <Card className="relative border-0 shadow-2xl hover:shadow-3xl transition-all duration-500 bg-gradient-to-br from-jamaica-green/5 via-white/90 to-jamaica-yellow/5 backdrop-blur-sm lg:scale-105 group">
                {/* Popular Badge - Overlaid on top of card */}
                <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 z-20">
                  <Badge className="bg-gradient-to-r from-jamaica-green to-jamaica-green/90 text-white font-bold px-6 py-3 shadow-2xl text-sm rounded-full border-2 border-white/50">
                    <Star className="w-4 h-4 mr-1" />
                    POPULAR
                  </Badge>
                </div>
                
                <div className="absolute inset-0 bg-gradient-to-br from-jamaica-green/10 via-transparent to-jamaica-yellow/10 rounded-2xl"></div>
              
              <CardContent className="relative p-6 pt-8">
                <div className="text-center mb-4">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Yearly</h3>
                  <div className="flex items-baseline justify-center gap-1 mb-2">
                    <span className="text-3xl font-bold bg-gradient-to-r from-jamaica-green to-jamaica-green/80 bg-clip-text text-transparent">
                      {formatDisplayAmount(getConvertedItemPrice(RUSHD_PLAN_CONFIG.yearly.price))}
                    </span>
                    <span className="text-gray-500 font-medium">/year</span>
                  </div>
                  
                  {/* Savings Display */}
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <span className="text-xs text-gray-400 line-through">
                      {formatDisplayAmount(getConvertedItemPrice(12 * RUSHD_PLAN_CONFIG.monthly.price))}/year
                    </span>
                    <span className="text-xs font-bold text-white bg-gradient-to-r from-green-500 to-green-600 px-2 py-0.5 rounded-full shadow-lg">
                      Save {formatDisplayAmount(getConvertedItemPrice(RUSHD_PLAN_CONFIG.yearly.monthlySavings))}
                    </span>
                  </div>
                  <p className="text-xs text-gray-600">{RUSHD_PLAN_CONFIG.yearly.description}</p>
                </div>
                
                {/* Features List */}
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-3 p-2.5 bg-white/50 rounded-lg hover:bg-white/70 transition-colors duration-200 border border-white/50">
                    <div className="flex-shrink-0 w-5 h-5 bg-gradient-to-br from-jamaica-green to-jamaica-green/80 rounded-lg flex items-center justify-center shadow-sm">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 text-xs">Discounted Express Shipping</p>
                      <p className="text-xs text-gray-600">Save up to 75% on Express shipping rates</p>
                    </div>
                  </div>
                  {RUSHD_PERKS.map((perk) => (
                    <div key={perk.id} className="flex items-center gap-3 p-2.5 bg-white/50 rounded-lg hover:bg-white/70 transition-colors duration-200 border border-white/50">
                      <div className="flex-shrink-0 w-5 h-5 bg-gradient-to-br from-jamaica-green to-jamaica-green/80 rounded-lg flex items-center justify-center shadow-sm">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 text-xs">{perk.name}</p>
                        <p className="text-xs text-gray-600">{perk.description}</p>
                      </div>
                    </div>
                  ))}
                  <div className="flex items-center gap-3 p-2.5 bg-gradient-to-r from-jamaica-yellow/10 to-jamaica-yellow/20 rounded-lg border border-jamaica-yellow/30">
                    <div className="flex-shrink-0 w-5 h-5 bg-gradient-to-br from-jamaica-yellow to-jamaica-yellow/80 rounded-lg flex items-center justify-center shadow-sm">
                      <Sparkles className="w-3 h-3 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-jamaica-green text-xs">Annual Savings</p>
                      <p className="text-xs text-gray-600">Over 50% savings compared to monthly billing</p>
                    </div>
                  </div>
                </div>
                
                <Button 
                  className="w-full bg-gradient-to-r from-jamaica-green to-jamaica-green/90 hover:from-jamaica-green/90 hover:to-jamaica-green text-white py-3 text-base font-semibold rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 group"
                  onClick={() => handlePlanSelect('yearly')}
                  disabled={loadingPlan === 'yearly' || (isMember && isActive)}
                >
                  {loadingPlan === 'yearly' ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : isMember && isActive && membershipStatus?.plan === 'yearly' ? (
                    'Current Plan'
                  ) : (
                    <>
                      Get Started - Best Value
                      <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform duration-200" />
                    </>
                  )}
                </Button>
              </CardContent>
              </Card>
            </div>
          </div>
          
          
          
          {/* FAQ Section */}
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
              Frequently Asked Questions
            </h2>
            
            <div className="space-y-6">
              <Card className="p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Can I cancel anytime?</h3>
                <p className="text-gray-600">
                  Yes! You can cancel your Rush'd membership anytime through your account settings. 
                  You'll retain access to member benefits until the end of your current billing period.
                </p>
              </Card>
              
              <Card className="p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Do savings apply to all shipping methods?</h3>
                <p className="text-gray-600">
                  Rush'd member pricing applies specifically to Express shipping. Standard shipping 
                  rates remain the same for all customers.
                </p>
              </Card>
              
              <Card className="p-6">
                <h3 className="font-semibold text-gray-900 mb-2">What are "Random Free Treats"?</h3>
                <p className="text-gray-600">
                  We love surprising our Rush'd members! Random free treats are bonus items we occasionally 
                  add to your orders at no extra cost - think sample-sized products, small gifts, or special Jamaica-themed items.
                </p>
              </Card>
              
              <Card className="p-6">
                <h3 className="font-semibold text-gray-900 mb-2">How does priority packing work?</h3>
                <p className="text-gray-600">
                  Rush'd member orders are flagged for priority processing in our fulfillment center, 
                  meaning they're packed and shipped ahead of regular orders during busy periods.
                </p>
              </Card>
            </div>
          </div>
        </div>
      </div>
      
      {/* Auth Modal */}
      <AuthModalWrapper
        isOpen={authModalOpen}
        onOpenChange={setAuthModalOpen}
        defaultTab="login"
      />
    </>
  );
};

export default RushdPricing;